import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cx } from '@/lib/clases'

export type VarianteBoton = 'primario' | 'secundario' | 'discreto' | 'destructivo'
export type TamanoBoton = 'normal' | 'menor'

export interface PropsBoton extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBoton
  tamano?: TamanoBoton
  /** Icono de trazo de 16px. Acompaña al texto; no lo sustituye. */
  icono?: ReactNode
  /** Atajo que se muestra a la derecha, como en el menú. `Ctrl+⏎`. */
  atajo?: string
  /** Ocupa el ancho disponible. Solo en paneles estrechos y en móvil. */
  ancho?: boolean
}

const POR_VARIANTE: Record<VarianteBoton, string> = {
  primario: 'bg-marca text-white border-marca hover:bg-marca-fuerte hover:border-marca-fuerte',
  secundario: 'bg-white text-gris-900 border-gris-500 hover:bg-gris-50',
  discreto: 'bg-transparent text-gris-700 border-transparent hover:bg-gris-100',
  // Rojo solo aquí: nunca en un botón que no destruya.
  destructivo: 'bg-rojo text-white border-rojo hover:brightness-95',
}

const POR_TAMANO: Record<TamanoBoton, string> = {
  normal: 'h-[var(--alto-control)] px-3 gap-2',
  menor: 'h-[var(--alto-control-menor)] px-2 gap-1 text-etiqueta',
}

/**
 * Botón de 36px de alto, radio de 6px, borde de 1px.
 * El foco lo resuelve la regla global de `:focus-visible`: 2px, siempre visible.
 */
export const Boton = forwardRef<HTMLButtonElement, PropsBoton>(function Boton(
  { variante = 'secundario', tamano = 'normal', icono, atajo, ancho, className, children, type, ...resto },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cx(
        'inline-flex items-center justify-center rounded-md border text-cuerpo font-medium',
        'transition-colors duration-[var(--duracion-corta)]',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-inherit',
        POR_VARIANTE[variante],
        POR_TAMANO[tamano],
        ancho === true && 'w-full',
        className,
      )}
      {...resto}
    >
      {icono ? (
        <span aria-hidden="true" className="shrink-0 [&>svg]:size-4">
          {icono}
        </span>
      ) : null}
      {children}
      {atajo ? (
        <span
          aria-hidden="true"
          className={cx(
            'ml-1 mono text-[11px] opacity-70',
            variante === 'primario' || variante === 'destructivo' ? 'text-white' : 'text-gris-600',
          )}
        >
          {atajo}
        </span>
      ) : null}
    </button>
  )
})

export interface PropsBotonIcono extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Obligatoria: un icono sin nombre accesible es un botón mudo. */
  etiqueta: string
  icono: ReactNode
  tamano?: TamanoBoton
}

/**
 * Botón de solo icono. Se usa únicamente en acciones universales
 * (cerrar, copiar, ordenar) y siempre lleva nombre accesible.
 */
export const BotonIcono = forwardRef<HTMLButtonElement, PropsBotonIcono>(function BotonIcono(
  { etiqueta, icono, tamano = 'normal', className, type, ...resto },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={etiqueta}
      title={etiqueta}
      className={cx(
        'inline-flex items-center justify-center rounded-md border border-transparent',
        'text-gris-600 hover:bg-gris-100 hover:text-gris-900',
        'transition-colors duration-[var(--duracion-corta)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        tamano === 'normal'
          ? 'size-[var(--alto-control)] [&>svg]:size-4'
          : 'size-[var(--alto-control-menor)] [&>svg]:size-4',
        className,
      )}
      {...resto}
    >
      {icono}
    </button>
  )
})
